import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Loader2, Play, RefreshCw, ExternalLink, Star, Check, X, Filter, Copy } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function DomainHunter() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending_review");
  const [minScore, setMinScore] = useState(0);
  const [loading, setLoading] = useState(true);
  const [scanState, setScanState] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [listR, statusR] = await Promise.all([
        axios.get(`${API}/api/admin/domain-hunter/queue?status=${status}&min_score=${minScore}&limit=200`),
        axios.get(`${API}/api/admin/domain-hunter/scan-status`),
      ]);
      setItems(listR.data.items || []);
      setScanState(statusR.data);
    } catch (e) {
      toast.error(`Błąd: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, minScore]);

  useEffect(() => {
    if (!scanState?.running) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [scanState?.running]);

  const triggerScan = async () => {
    if (!confirm("Uruchomić skan wygasających domen? Analiza ~100 domen zajmuje kilka minut.")) return;
    setTriggering(true);
    try {
      const r = await axios.post(`${API}/api/admin/domain-hunter/scan`, { max_analyze: 100 });
      if (r.data?.error) toast.error(r.data.error);
      else toast.success(`Skan uruchomiony (job ${r.data.job_id?.slice(0,8)}, do ${r.data.max_analyze} domen)`);
      await load();
    } catch (e) {
      toast.error(`Błąd: ${e?.response?.data?.detail || e.message}`);
    } finally { setTriggering(false); }
  };

  const copyDomains = async () => {
    const list = items.map(it => it.domain).join("\n");
    if (!list) { toast.info("Brak domen do skopiowania"); return; }
    try {
      await navigator.clipboard.writeText(list);
      toast.success(`Skopiowano ${items.length} domen do schowka`);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = list;
      document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success(`Skopiowano ${items.length} domen`); }
      catch { toast.error("Nie udalo sie skopiowac"); }
      document.body.removeChild(ta);
    }
  };

  const openPageRankChecker = () => {
    window.open("https://openpagerank.keywordseverywhere.com/check", "_blank", "noopener");
    if (items.length > 0) {
      toast.info("Uzyj przycisku Kopiuj obok i wklej domeny na tej stronie");
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.post(`${API}/api/admin/domain-hunter/queue/${id}/status`, { status: newStatus });
      toast.success(`Zmieniono na: ${newStatus}`);
      if (selected?.id === id) setSelected(null);
      await load();
    } catch (e) {
      toast.error(`Błąd: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const scoreColor = (s) => s >= 80 ? "bg-emerald-500" : s >= 60 ? "bg-amber-500" : s >= 40 ? "bg-orange-500" : "bg-slate-500";
  const potentialColor = (p) => p === "wysoki" ? "text-emerald-600" : p === "sredni" || p === "średni" ? "text-amber-600" : "text-slate-500";

  return (
    <div className="space-y-6 max-w-none">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Star className="w-6 h-6 text-amber-500" />
            Domain Hunter — łowca domen
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Codzienny skan RSS aftermarket.pl → filtr → Wayback → Grok analiza (temat + pomysł biznesowy) → ranking hunter_score 0-100.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Odśwież
          </Button>
          <Button variant="outline" size="sm" onClick={copyDomains} disabled={items.length === 0}
                  title="Skopiuj wszystkie widoczne domeny do schowka (jedna pod drugą)">
            <Copy className="w-4 h-4 mr-1" />
            Kopiuj ({items.length})
          </Button>
          <Button variant="outline" size="sm" onClick={openPageRankChecker}
                  className="gap-1 text-blue-600 border-blue-300 hover:bg-blue-50"
                  title="Otwórz OpenPageRank checker w nowej karcie">
            <ExternalLink className="w-4 h-4" />
            OpenPageRank
          </Button>
          <Button size="sm" onClick={triggerScan} disabled={triggering || scanState?.running}
                  className="bg-amber-600 hover:bg-amber-700 gap-1">
            {(triggering || scanState?.running) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {scanState?.running ? "Skan w toku…" : "Uruchom skan"}
          </Button>
        </div>
      </div>

      {/* Status ostatniego skanu */}
      {scanState?.last_history && (
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground mb-1">Ostatni skan</div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3 text-sm">
              <div><span className="text-muted-foreground">RSS:</span> <strong>{scanState.last_history.rss_total}</strong></div>
              <div><span className="text-muted-foreground">Pre-filter:</span> <strong>{scanState.last_history.pre_filtered}</strong></div>
              <div><span className="text-muted-foreground">Analiz:</span> <strong>{scanState.last_history.analyzed}</strong></div>
              <div><span className="text-muted-foreground">Wayback:</span> <strong className="text-emerald-600">{scanState.last_history.with_wayback}</strong></div>
              <div><span className="text-muted-foreground">Zapisane:</span> <strong className="text-amber-600">{scanState.last_history.saved}</strong></div>
              <div><span className="text-muted-foreground">Błędy:</span> <strong>{scanState.last_history.errors}</strong></div>
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              {new Date(scanState.last_history.finished_at || scanState.last_history.started_at).toLocaleString("pl-PL")}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtry */}
      <Card>
        <CardContent className="p-3 flex items-center gap-3 flex-wrap">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <div className="flex gap-1.5">
            {["pending_review", "interested", "bought", "rejected", "all"].map((s) => (
              <button key={s} onClick={() => setStatus(s)}
                      className={`px-3 py-1 rounded text-xs font-medium border ${status === s ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                {s === "pending_review" ? "Do przeglądu" : s === "interested" ? "Zainteresowany" : s === "bought" ? "Kupione" : s === "rejected" ? "Odrzucone" : "Wszystkie"}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 ml-auto">
            <span className="text-xs text-muted-foreground">min score:</span>
            <Input type="number" value={minScore} onChange={(e) => setMinScore(Number(e.target.value))}
                   className="w-16 h-7 text-xs" min={0} max={100} />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-muted-foreground">
          Brak wpisów. {status === "pending_review" && "Uruchom skan aby znaleźć wygasające domeny."}
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((it) => {
            const w = it.wayback || {};
            const g = it.grok || {};
            const score = it.hunter_score || 0;
            return (
              <Card key={it.id} className="cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => setSelected(it)}>
                <CardContent className="p-4 space-y-2">
                  {/* Header — domena + score */}
                  <div className="flex items-center justify-between gap-2">
                    <a href={`https://www.aftermarket.pl/Deleted/Information/?domain=${it.domain}`}
                       target="_blank" rel="noopener"
                       onClick={(e) => e.stopPropagation()}
                       className="font-mono text-sm font-bold text-blue-600 hover:underline truncate">
                      {it.domain}
                    </a>
                    <div className={`px-2 py-0.5 rounded text-white text-xs font-bold shrink-0 ${scoreColor(score)}`}>
                      {score}
                    </div>
                  </div>

                  {/* Meta wiersz */}
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <span>wiek: <strong>{it.age_years}L</strong></span>
                    <span>·</span>
                    <span>{w.snapshot_count || 0} snap</span>
                    {w.oldest_snapshot_year && <><span>·</span><span>od {w.oldest_snapshot_year}</span></>}
                    <span>·</span>
                    <span>SEO: <span className={`font-semibold ${potentialColor(g.seo_potential)}`}>{g.seo_potential || "?"}</span></span>
                  </div>

                  {/* Topic */}
                  <div className="text-xs">
                    <div className="text-[10px] uppercase text-muted-foreground">Temat</div>
                    <div className="font-medium">{g.topic || "?"}</div>
                    {g.industry && <Badge variant="outline" className="text-[9px] mt-1">{g.industry}</Badge>}
                  </div>

                  {/* Summary preview */}
                  <div className="text-[11px] text-muted-foreground line-clamp-3 pt-1 border-t">
                    {g.summary || ""}
                  </div>

                  {/* Wygasa */}
                  <div className="text-[10px] text-red-600 pt-1">
                    Wygasa: {new Date(it.expires_at).toLocaleString("pl-PL", {day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"})}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal detail */}
      {selected && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
             onClick={() => setSelected(null)}>
          <div className="bg-background rounded-lg shadow-xl max-w-none w-full max-h-[90vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-bold font-mono">{selected.domain}</h2>
                    <div className={`px-3 py-1 rounded text-white font-bold ${scoreColor(selected.hunter_score)}`}>
                      score {selected.hunter_score}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Wiek: {selected.age_years} lat · Wygasa: {new Date(selected.expires_at).toLocaleString("pl-PL")} · Rejestrator: {selected.registrar}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelected(null)}><X className="w-4 h-4" /></Button>
              </div>

              {/* Wayback info */}
              <div className="bg-slate-50 dark:bg-slate-900 rounded p-3 space-y-1">
                <div className="text-xs font-semibold">Wayback Machine</div>
                <div className="text-xs text-muted-foreground">
                  {selected.wayback?.snapshot_count} snapshotów · {selected.wayback?.oldest_snapshot_year || "?"} → {selected.wayback?.latest_snapshot_year || "?"}
                </div>
                {selected.wayback?.latest_snapshot_url && (
                  <a href={selected.wayback.latest_snapshot_url} target="_blank" rel="noopener"
                     className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">
                    Otwórz najnowszy snapshot <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>

              {/* Grok analiza */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold">Analiza AI</h3>
                <div className="text-xs">
                  <div className="text-[10px] uppercase text-muted-foreground">Temat</div>
                  <div>{selected.grok?.topic}</div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>Branża: <Badge variant="outline" className="ml-1">{selected.grok?.industry}</Badge></div>
                  <div>Content score: <strong>{selected.grok?.content_score}/100</strong></div>
                  <div>SEO: <strong className={potentialColor(selected.grok?.seo_potential)}>{selected.grok?.seo_potential}</strong></div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground">Podsumowanie</div>
                  <div className="text-xs">{selected.grok?.summary}</div>
                </div>
                <div className="bg-amber-50 dark:bg-amber-950/30 border-l-4 border-amber-500 p-3 rounded">
                  <div className="text-[10px] uppercase text-amber-800 dark:text-amber-400 font-semibold">💡 Propozycja biznesowa</div>
                  <div className="text-xs font-medium text-amber-900 dark:text-amber-100 mt-1">{selected.grok?.business_idea}</div>
                </div>
              </div>

              {/* Sample content */}
              {selected.content_sample && (
                <details className="text-xs">
                  <summary className="cursor-pointer font-semibold text-muted-foreground">Sample z ostatniego snapshotu</summary>
                  <pre className="mt-2 p-2 bg-slate-100 dark:bg-slate-900 rounded text-[10px] max-h-32 overflow-y-auto whitespace-pre-wrap">{selected.content_sample}</pre>
                </details>
              )}

              {/* Akcje */}
              <div className="flex flex-wrap justify-between gap-2 pt-3 border-t">
                <a href={`https://www.aftermarket.pl/Deleted/Information/?domain=${selected.domain}`}
                   target="_blank" rel="noopener"
                   className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
                  <ExternalLink className="w-4 h-4" /> Kup na Aftermarket
                </a>
                <div className="flex gap-2">
                  <Button variant="destructive" size="sm" onClick={() => updateStatus(selected.id, "rejected")}>
                    <X className="w-3.5 h-3.5 mr-1" /> Odrzuć
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => updateStatus(selected.id, "interested")}>
                    <Star className="w-3.5 h-3.5 mr-1" /> Zainteresowany
                  </Button>
                  <Button size="sm" onClick={() => updateStatus(selected.id, "bought")}
                          className="bg-emerald-600 hover:bg-emerald-700">
                    <Check className="w-3.5 h-3.5 mr-1" /> Kupione
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
